import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

/**
 * Reusable ActionButtons component for consistent button styling and behavior
 * 
 * @param {Array} buttons - Array of button configurations
 * @param {Object} selectedRecord - Currently selected record (for conditional button display)
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable all buttons
 * 
 * Button configuration object:
 * @param {string} type - Button type identifier
 * @param {Object} icon - FontAwesome icon
 * @param {Function} onClick - Click handler
 * @param {string} title - Button title/tooltip (translation key)
 * @param {boolean} disabled - Disable this specific button
 * @param {string} showWhen - When to show button: 'always', 'whenSelected', 'whenNotSelected'
 * @param {string} className - Additional CSS classes for this button
 * 
 * Example usage:
 * <ActionButtons
 *   buttons={[
 *     { ...COMMON_BUTTONS.ADD, onClick: handleAdd },
 *     { ...COMMON_BUTTONS.EDIT, onClick: handleEdit },
 *     { ...COMMON_BUTTONS.DELETE, onClick: handleDelete }
 *   ]}
 *   selectedRecord={selectedRecord}
 * />
 */
export default function ActionButtons({ 
    buttons = [], 
    selectedRecord = null,
    className = "",
    disabled = false 
}) {
    const { t } = useTranslation();

    const renderButton = (button) => {
        const {
            type,
            icon,
            iconImg,
            onClick,
            title,
            disabled: buttonDisabled = false,
            showWhen = 'always', // 'always', 'whenSelected', 'whenNotSelected'
            className: buttonClassName = '',
            iconProps = {},
            ...otherProps
        } = button;

        // Determine if button should be shown
        let shouldShow = true;
        if (showWhen === 'whenSelected' && !selectedRecord) {
            shouldShow = false;
        } else if (showWhen === 'whenNotSelected' && selectedRecord) {
            shouldShow = false;
        }

        if (!shouldShow) return null;

        // Determine if button should be disabled
        const isDisabled = disabled || buttonDisabled || (showWhen === 'whenSelected' && !selectedRecord);

        return (
            <button
                key={type || title}
                className={`icon-button ${!selectedRecord && showWhen === 'whenSelected' ? 'disabled-link' : ''} ${buttonClassName}`}
                onClick={onClick}
                disabled={isDisabled}
                title={t(title)}
                {...otherProps}
            >
                {iconImg ? (
                    <img src={iconImg} alt="" className="action-button-img" {...iconProps} />
                ) : (
                    icon && <FontAwesomeIcon icon={icon} {...iconProps} />
                )}
            </button>
        );
    };

    return (
        <div className={`action-buttons ${className}`}>
            {buttons.map(renderButton)}
        </div>
    );
}